import { observable, action, computed, runInAction } from "mobx";
import agent from "../agent";
import templateStore from "./templateStore";

class HgSuperPayStore {
    @observable employeeStatus = {};
    @observable employerStatus = null;
    @observable isLoading = false;

    @computed get isEmployerActive() {
        const s = this.employerStatus;
        if (!s || !s.onboarded) return false;
        const onboarded = !!(s.onboardedAtClearingHouse ?? (s.employerData && s.employerData.onboardedAtClearingHouse));
        const synced = s.detailsSyncedAtClearingHouse ?? (s.employerData && s.employerData.detailsSyncedAtClearingHouse);
        return onboarded && (synced === "true" || synced === "NA");
    }
    @observable validationResult = null;
    @observable paymentResult = null;
    @observable contributionHistory = [];
    @observable isLoadingHistory = false;

    @action
    resetState() {
        this.employeeStatus = {};
        this.validationResult = null;
        this.paymentResult = null;
    }

    @action
    getEmployeeStatus(profileId) {
        this.isLoading = true;
        return agent.HgSuperPay.getEmployeeStatus(profileId)
            .then(
                action(response => {
                    this.employeeStatus = response.data || response || {};
                    return this.employeeStatus;
                })
            )
            .catch(
                action(error => {
                    this.employeeStatus = { registered: false };
                    throw error;
                })
            )
            .finally(() => {
                this.isLoading = false;
            });
    }

    @action
    async getEmployerStatus(profileId, venueId) {
        this.isLoading = true;
        try {
            const response = await agent.HgSuperPay.getEmployerStatus(profileId, venueId);
            const raw = response.data || response || {};
            const status = raw.response || raw;
            runInAction(() => {
                this.employerStatus = status.onboarded !== undefined ? status : { onboarded: false };
            });
            return this.employerStatus;
        } catch (e) {
            runInAction(() => {
                this.employerStatus = { onboarded: false };
            });
        } finally {
            runInAction(() => { this.isLoading = false; });
        }
    }

    @action
    async onboardEmployer(data, isUpdate = false) {
        this.isLoading = true;
        templateStore.showLoadingScreen();
        try {
            const response = await agent.HgSuperPay.onboardEmployer(data);
            runInAction(() => {
                this.employerStatus = {
                    onboarded: true,
                    employerId: response.data?.employerId || response?.employerId
                };
            });
            templateStore.openSnackbar(isUpdate ? "Employer updated successfully" : "Employer registered successfully");
            return response;
        } catch (e) {
            const rawErrors = e?.response?.data?.errors;
            let msg;
            if (rawErrors && typeof rawErrors === "object" && !Array.isArray(rawErrors)) {
                msg = Object.values(rawErrors).flat().join(", ");
            } else {
                msg = rawErrors || e?.message || "Employer registration failed. Please try again.";
            }
            templateStore.openSnackbar(msg);
            throw e;
        } finally {
            runInAction(() => { this.isLoading = false; });
            templateStore.hideLoadingScreen();
        }
    }

    @action
    syncEmployee(profileId, employerProfileId) {
        this.isLoading = true;
        templateStore.showLoadingScreen();
        return agent.HgSuperPay.syncEmployee(profileId, employerProfileId)
            .then(
                action(response => {
                    const data = response.data || response;
                    const result = data.response || data;
                    if (result.success === false) {
                        throw new Error(result.message || result.error || "Failed to sync employee");
                    }
                    this.employeeStatus = {
                        registered: true,
                        employeeId: result.employeeId,
                        mvrStatus: result.mvrStatus,
                        mvrStatusReason: result.mvrStatusReason
                    };
                    return result;
                })
            )
            .catch(
                action(error => {
                    const rawErrors = error?.response?.data?.errors;
                    const msg = (typeof rawErrors === "string" && rawErrors)
                        ? rawErrors
                        : (rawErrors && typeof rawErrors === "object"
                            ? Object.values(rawErrors).flat().join(", ")
                            : error?.response?.data?.message || error?.message || "Failed to sync employee");
                    templateStore.openSnackbar(msg);
                    throw error;
                })
            )
            .finally(() => {
                this.isLoading = false;
                templateStore.hideLoadingScreen();
            });
    }

    @action
    getEmployeeMapping(profileId, page = 1, limit = 20, search = "", mapped = null) {
        return agent.HgSuperPay.getEmployeeMapping(profileId, page, limit, search, mapped)
            .then(response => {
                const data = response.data || response;
                const payload = data.response || data;
                const items = Array.isArray(payload.contacts)
                    ? payload.contacts
                    : (Array.isArray(payload.items) ? payload.items : (Array.isArray(payload) ? payload : []));
                const apiPagination = payload.pagination || {};
                const currentPage = Number(apiPagination.page) || page;
                const currentLimit = Number(apiPagination.limit) || limit;
                const total = apiPagination.total != null
                    ? Number(apiPagination.total)
                    : (payload.total != null ? Number(payload.total) : items.length);
                const totalPages = Number(apiPagination.totalPages) || Math.max(1, Math.ceil(total / currentLimit));
                const pagination = {
                    page: currentPage,
                    limit: currentLimit,
                    total,
                    totalPages,
                    hasNext: apiPagination.hasNext != null ? apiPagination.hasNext : currentPage < totalPages,
                    hasPrevious: apiPagination.hasPrevious != null ? apiPagination.hasPrevious : currentPage > 1
                };

                // Keep `items` for existing callers such as contribution history.
                return { items, contacts: items, total, pagination };
            })
            .catch(error => {
                throw error;
            });
    }

    @action
    getEmployeeSuper(profileId) {
        return agent.HgSuperPay.getEmployeeSuper(profileId)
            .then(action(response => response.data || response));
    }

    @action
    getEmployeePreview(contactId, employerProfileId) {
        return agent.HgSuperPay.getEmployeePreview(contactId, employerProfileId)
            .then(action(response => response.data || response));
    }

    @action
    deleteEmployeeMapping(profileId, employerProfileId) {
        this.isLoading = true;
        templateStore.showLoadingScreen();
        return agent.HgSuperPay.deleteEmployeeMapping(profileId, employerProfileId)
            .then(
                action(response => {
                    templateStore.openSnackbar("Employee mapping removed successfully");
                    return response.data || response;
                })
            )
            .catch(
                action(error => {
                    const msg = error?.response?.data?.message || error?.message || "Failed to remove employee mapping";
                    templateStore.openSnackbar(msg);
                    throw error;
                })
            )
            .finally(() => {
                this.isLoading = false;
                templateStore.hideLoadingScreen();
            });
    }

    @action
    updateEmployee(profileId, employerProfileId) {
        this.isLoading = true;
        templateStore.showLoadingScreen();
        return agent.HgSuperPay.updateEmployee(profileId, employerProfileId)
            .then(
                action(response => {
                    const data = response.data || response;
                    const result = data.response || data;
                    if (result.success === false) {
                        throw new Error(result.message || result.error || "Failed to update employee");
                    }
                    this.employeeStatus = {
                        registered: true,
                        employeeId: result.mygigstersEmployeeId || result.employeeId,
                        mvrStatus: result.mvrStatus,
                        mvrStatusReason: result.mvrStatusReason
                    };
                    return result;
                })
            )
            .catch(
                action(error => {
                    const msg = error?.response?.data?.message || error?.message || "Failed to update employee";
                    templateStore.openSnackbar(msg);
                    throw error;
                })
            )
            .finally(() => {
                this.isLoading = false;
                templateStore.hideLoadingScreen();
            });
    }

    @action
    submitMvr(profileId, employerProfileId) {
        return agent.HgSuperPay.submitMvr(profileId, employerProfileId)
            .then(action(response => {
                const data = response?.data || response;
                const result = data?.response || data;
                if (result?.success === false || Number(result?.status) >= 400 || typeof result?.mvrStatus !== "string" || !result.mvrStatus.trim()) {
                    throw new Error(result?.message || "MVR verification did not start.");
                }
                return result;
            }));
    }

    @action
    validatePayees(payeeIds) {
        return agent.HgSuperPay.validatePayees({ payeeIds })
            .then(action(response => {
                const data = response.data || response;
                return data.response || data;
            }))
            .catch(action(error => {
                throw error;
            }));
    }

    @action
    syncPaymentStatus(paymentId) {
        return agent.HgSuperPay.syncPaymentStatus(paymentId)
            .then(action(response => {
                const data = response.data || response;
                return data.response || data;
            }))
            .catch(action(error => {
                throw error;
            }));
    }

    @action
    getPaymentMemberOutcomes(paymentId) {
        return agent.HgSuperPay.getPaymentMemberOutcomes(paymentId)
            .then(action(response => {
                const data = response.data || response;
                return data.response || data;
            }))
            .catch(action(error => {
                throw error;
            }));
    }

    @action
    validateLedgers(superLedgerIds) {
        this.isLoading = true;
        templateStore.showLoadingScreen();
        return agent.HgSuperPay.validateLedgers({ superLedgerIds })
            .then(
                action(response => {
                    this.validationResult = response.data || response || { isValid: true };
                    return this.validationResult;
                })
            )
            .catch(
                action(error => {
                    const errorMessage = error?.response?.data?.message || error?.message || "Validation failed";
                    this.validationResult = { isValid: false, error: errorMessage };
                    throw error;
                })
            )
            .finally(() => {
                this.isLoading = false;
                templateStore.hideLoadingScreen();
            });
    }

    @action
    getEffectivePayer(profileId) {
        return agent.HgSuperPay.getEffectivePayer(profileId)
            .then(
                action(response => {
                    return response?.data || response;
                })
            );
    }

    @action
    async getContributionHistory(employeeId, startDate, endDate, employerProfileId, page, limit, organisationId) {
        this.isLoadingHistory = true;
        try {
            const response = await agent.HgSuperPay.getContributionHistory(employeeId, startDate, endDate, employerProfileId, page, limit, organisationId);
            const inner = response?.response || response?.data?.response || response?.data || response || {};
            const items = Array.isArray(inner.contributions) ? inner.contributions : (Array.isArray(inner) ? inner : []);
            const pagination = inner.pagination || null;
            runInAction(() => {
                this.contributionHistory = items;
            });
            return { contributions: items, pagination };
        } catch (e) {
            runInAction(() => {
                this.contributionHistory = [];
            });
            throw e;
        } finally {
            runInAction(() => { this.isLoadingHistory = false; });
        }
    }

    @action
    async submitPayment(data) {
        this.isLoading = true;
        templateStore.showLoadingScreen();
        try {
            // Resolve the effective payer before hitting the API so the store
            // owns this step and callers can pass the raw ledger payer ID directly.
            let payerId = data.payerId;
            if (payerId) {
                const effectivePayerResult = await agent.HgSuperPay.getEffectivePayer(payerId);
                payerId = (effectivePayerResult?.data || effectivePayerResult)?.effectivePayerId || payerId;
            }
            const response = await agent.HgSuperPay.submitPayment({ ...data, payerId });
            this.paymentResult = response?.data || response;
            templateStore.openSnackbar("Payment submitted successfully");
            return this.paymentResult;
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Payment submission failed";
            templateStore.openSnackbar(errorMessage);
            throw error;
        } finally {
            this.isLoading = false;
            templateStore.hideLoadingScreen();
        }
    }
}

export default new HgSuperPayStore();
